// src/Pages/Dashboard/Sales/ViewDelivery_Challans.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ViewDelivery_Challans.css";

const ViewDelivery_Challans = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [challan, setChallan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const backendUrl =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production"
      ? "https://finance.dev.marsforce.in"
      : "http://localhost:8030");

  useEffect(() => {
    const fetchChallan = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError("");

        const res = await fetch(`${backendUrl}/api/delivery-challans/${id}`, {
          method: "GET",
          credentials: "include",
        });

        const data = await res.json();

        if (data.success && data.data) {
          setChallan(data.data);
        } else {
          setError(data.error || "Delivery Challan not found");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError(
          "Failed to connect. Please check your internet or try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchChallan();
  }, [id, backendUrl]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(Number(amount || 0));
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  if (loading) {
    return (
      <div className="view-challan-page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading Delivery Challan...</p>
        </div>
      </div>
    );
  }

  if (error || !challan) {
    return (
      <div className="view-challan-page">
        <div className="error-state">
          <h2>Challan Not Found</h2>
          <p>
            {error ||
              "This delivery challan may have been deleted or doesn't exist."}
          </p>
          <button
            onClick={() => navigate("/sales/delivery-challans")}
            className="back-btn"
          >
            ← Back to List
          </button>
        </div>
      </div>
    );
  }

  const subtotal = challan.items.reduce(
    (sum, item) => sum + (item.amount || 0),
    0
  );
  const discountAmount =
    challan.discountType === "%"
      ? (subtotal * (challan.discountValue || 0)) / 100
      : challan.discountValue || 0;
  const total = subtotal - discountAmount + Number(challan.adjustment || 0);

  return (
    <div className="view-challan-page">
      {/* Header Bar */}
      <div className="challan-header-bar">
        <button
          onClick={() => navigate("/sales/delivery-challans")}
          className="back-btn"
        >
          ← All Delivery Challans
        </button>
        <div className="action-buttons">
          <button
            className="edit-btn"
            onClick={() => navigate(`/sales/delivery-challans/new?id=${id}`)}
          >
            Edit
          </button>
          <button className="print-btn" onClick={() => window.print()}>
            Print
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="challan-print-container">
        <div className="challan-content">
          {/* Title & Status */}
          <div className="challan-title-section">
            <div className="title-left">
              <h1>DELIVERY CHALLAN</h1>
            </div>
            <div className="title-right">
              <div className="challan-no">#{challan.challanNo}</div>
              <div
                className={`status-badge status-${(
                  challan.status || "DRAFT"
                ).toLowerCase()}`}
              >
                {challan.status || "DRAFT"}
              </div>
            </div>
          </div>

          {/* Info Grid */}
          <div className="challan-meta-grid">
            <div className="meta-item">
              <strong>Customer</strong>
              <p>{challan.customerName || "—"}</p>
            </div>
            <div className="meta-item">
              <strong>Challan Date</strong>
              <p>{formatDate(challan.challanDate)}</p>
            </div>
            <div className="meta-item">
              <strong>Reference #</strong>
              <p>{challan.referenceNo || "—"}</p>
            </div>
            <div className="meta-item">
              <strong>Challan Type</strong>
              <p>{challan.challanType || "Others"}</p>
            </div>
          </div>

          {/* Items Table */}
          <div className="items-section">
            <h3>Items Delivered</h3>
            <table className="items-table">
              <thead>
                <tr>
                  <th style={{ width: "45%" }}>Description</th>
                  <th style={{ width: "15%" }} className="text-right">
                    Qty
                  </th>
                  <th style={{ width: "20%" }} className="text-right">
                    Rate
                  </th>
                  <th style={{ width: "20%" }} className="text-right">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {challan.items?.length > 0 ? (
                  challan.items.map((item, i) => (
                    <tr key={i}>
                      <td>{item.description || "—"}</td>
                      <td className="text-right">{item.qty || 0}</td>
                      <td className="text-right">
                        {formatCurrency(item.rate)}
                      </td>
                      <td className="text-right">
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No items added
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="totals-section">
            <div className="totals-card">
              <div className="total-row">
                <span>Subtotal</span>
                <strong>{formatCurrency(subtotal)}</strong>
              </div>
              {challan.discountValue > 0 && (
                <div className="total-row discount">
                  <span>
                    Discount ({challan.discountValue}
                    {challan.discountType})
                  </span>
                  <strong>-{formatCurrency(discountAmount)}</strong>
                </div>
              )}
              {challan.adjustment != null && challan.adjustment !== 0 && (
                <div className="total-row">
                  <span>Adjustment</span>
                  <strong>{formatCurrency(challan.adjustment)}</strong>
                </div>
              )}
              <div className="grand-total-row">
                <span>Total Amount</span>
                <strong className="grand-total">{formatCurrency(total)}</strong>
              </div>
            </div>
          </div>

          {/* Notes & Terms */}
          {(challan.customerNotes || challan.termsConditions) && (
            <div className="notes-terms-section">
              {challan.customerNotes && (
                <div className="note-box">
                  <strong>Customer Notes</strong>
                  <p>{challan.customerNotes}</p>
                </div>
              )}
              {challan.termsConditions && (
                <div className="note-box">
                  <strong>Terms & Conditions</strong>
                  <p>{challan.termsConditions}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewDelivery_Challans;
